import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  CreditCard,
  RefreshCcw,
  Ban,
  Pencil,
  ExternalLink,
  CheckCircle2,
  AlertTriangle,
  Sparkles,
  Receipt,
  ScrollText,
} from "lucide-react";
import { toast } from "sonner";
import JeaCard from "@/components/jea/JeaCard";
import JeaButton from "@/components/jea/JeaButton";
import EmptyState from "@/components/jea/EmptyState";
import Skeleton from "@/components/jea/Skeleton";
import ChangePlanModal from "@/components/billing/ChangePlanModal";
import PaymentMethodModal from "@/components/billing/PaymentMethodModal";
import CancelSubscriptionModal from "@/components/billing/CancelSubscriptionModal";
import { apiClient, formatApiError } from "@/lib/apiClient";
import { downloadAuthFile } from "@/lib/downloadFile";
import { centsToUSD, shortDate, relativeTime } from "@/lib/dates";
import { cn } from "@/lib/utils";

const PATH_LABEL = { A: "Standard — 3 Months", B: "Standard — 6 Months", C: "Standard — 12 Months" };
const PRO_CADENCE_LABEL = { monthly: "PRO · Monthly", annual: "PRO · Annual" };

const planLabel = (sub) => {
  if (!sub) return "—";
  if (sub.plan_type === "pro") return PRO_CADENCE_LABEL[sub.path] || "PRO";
  return `${PATH_LABEL[sub.path] || "Standard"} · ${sub.debtor_count} debtor${sub.debtor_count === 1 ? "" : "s"}`;
};

const StatusPill = ({ status }) => {
  const map = {
    active:     { bg: "bg-success/15", text: "text-success", border: "border-success/40", label: "Active" },
    canceling:  { bg: "bg-warning/15", text: "text-warning", border: "border-warning/40", label: "Canceling" },
    past_due:   { bg: "bg-danger/15",  text: "text-danger",  border: "border-danger/40",  label: "Past Due" },
    canceled:   { bg: "bg-bg-tertiary", text: "text-text-secondary", border: "border-border-default", label: "Canceled" },
  };
  const cfg = map[status] || map.canceled;
  return (
    <span className={cn("inline-flex px-2.5 py-1 rounded-full border text-[11px] font-bold tracking-[0.06em] uppercase", cfg.bg, cfg.text, cfg.border)} data-testid="billing-plan-status">
      {cfg.label}
    </span>
  );
};

const PaymentMethodDisplay = ({ pm, onEdit }) => {
  if (!pm) {
    return (
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-[8px] bg-bg-primary border border-border-default flex items-center justify-center">
            <CreditCard size={18} className="text-text-secondary" />
          </div>
          <div>
            <div className="text-white text-[14px] font-semibold">No card on file</div>
            <div className="text-text-secondary text-[12px]">Add a payment method to enable auto-renewal.</div>
          </div>
        </div>
        <JeaButton size="sm" variant="outline-pink" onClick={onEdit} className="gap-2" data-testid="billing-add-card">
          <Pencil size={12} /> Add Card
        </JeaButton>
      </div>
    );
  }
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-[8px] bg-bg-primary border border-border-default flex items-center justify-center">
          <CreditCard size={20} className="text-accent-pink" />
        </div>
        <div>
          <div className="text-white text-[14px] font-semibold" data-testid="billing-card-brand">
            {pm.brand} ···· <span className="jea-mono">{pm.last4}</span>
          </div>
          <div className="text-text-secondary text-[12px]">
            Expires {String(pm.exp_month).padStart(2, "0")}/{String(pm.exp_year).slice(-2)}
          </div>
        </div>
      </div>
      <JeaButton size="sm" variant="outline-neutral" onClick={onEdit} className="gap-2" data-testid="billing-edit-card">
        <Pencil size={12} /> Edit
      </JeaButton>
    </div>
  );
};

const HistoryTable = ({ events, loading }) => {
  if (loading) {
    return (
      <JeaCard className="p-6 space-y-3">
        {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-10" />)}
      </JeaCard>
    );
  }
  if (!events || events.length === 0) {
    return (
      <EmptyState
        icon={Receipt}
        title="No billing history yet"
        description="Subscription payments, plan changes, and refunds will appear here."
        testid="billing-history-empty"
      />
    );
  }
  const kindLabel = (k) => ({
    subscription: "Subscription",
    plan_change: "Plan Change",
    broker_query: "Broker Query",
    refund: "Refund",
    manual_adjustment: "Adjustment",
  })[k] || k;

  return (
    <JeaCard className="p-0 overflow-hidden" data-testid="billing-history-table">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border-default">
              <th className="jea-caption text-text-secondary p-4 text-left">Date</th>
              <th className="jea-caption text-text-secondary p-4 text-left">Description</th>
              <th className="jea-caption text-text-secondary p-4 text-left">Type</th>
              <th className="jea-caption text-text-secondary p-4 text-left">Status</th>
              <th className="jea-caption text-text-secondary p-4 text-right">Amount</th>
              <th className="jea-caption text-text-secondary p-4 text-right">Receipt</th>
            </tr>
          </thead>
          <tbody>
            {events.map((e, i) => (
              <motion.tr
                key={e.id}
                initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.18, delay: i * 0.02 }}
                className="border-b border-border-default hover:bg-bg-tertiary transition-colors"
                data-testid={`billing-history-row-${e.id}`}
              >
                <td className="p-4 jea-date text-[13px]">{shortDate(e.created_at)}</td>
                <td className="p-4 text-white text-[13px] font-medium">{e.description}</td>
                <td className="p-4">
                  <span className="text-[11px] tracking-[0.06em] uppercase text-text-secondary font-semibold">
                    {kindLabel(e.kind)}
                  </span>
                </td>
                <td className="p-4">
                  <span className={cn(
                    "inline-flex px-2 py-1 rounded-full text-[10px] font-bold tracking-[0.06em] uppercase border",
                    e.status === "paid" ? "bg-success/15 text-success border-success/40"
                    : e.status === "pending" ? "bg-warning/15 text-warning border-warning/40"
                    : e.status === "refunded" ? "bg-bg-tertiary text-text-secondary border-border-default"
                    : "bg-danger/15 text-danger border-danger/40",
                  )}>
                    {e.status || "—"}
                  </span>
                </td>
                <td className="p-4 jea-mono text-right text-accent-pink font-bold text-[13px]" data-testid={`billing-history-amount-${e.id}`}>
                  {centsToUSD(e.amount_cents)}
                </td>
                <td className="p-4 text-right">
                  <button
                    type="button"
                    onClick={async () => {
                      try { await downloadAuthFile(`/billing/events/${e.id}/receipt`); }
                      catch (err) { toast.error(formatApiError(err, "Couldn't download receipt.")); }
                    }}
                    className="text-text-secondary hover:text-accent-pink transition-colors inline-flex items-center gap-1 text-[12px] font-semibold"
                    data-testid={`billing-history-receipt-${e.id}`}
                  >
                    PDF <ExternalLink size={12} />
                  </button>
                </td>
              </motion.tr>
            ))}
          </tbody>
        </table>
      </div>
    </JeaCard>
  );
};

const PerQueryTable = ({ charges, totalCents, loading, periodStart, onRowClick }) => {
  if (loading) {
    return (
      <JeaCard className="p-6 space-y-3">
        {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-10" />)}
      </JeaCard>
    );
  }
  return (
    <JeaCard className="p-0 overflow-hidden" data-testid="per-query-table">
      <div className="px-5 py-4 border-b border-border-default flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="jea-caption">Current Billing Period</div>
          <h3 className="text-white font-semibold text-[15px] mt-0.5">Per-Query Broker Charges</h3>
          <div className="text-text-muted text-[11px] mt-0.5">
            {periodStart ? `Since ${shortDate(periodStart)}` : "All-time"}
          </div>
        </div>
        <div className="text-right">
          <div className="jea-caption">Period Total</div>
          <div className="jea-mono text-accent-pink font-extrabold text-[24px]" data-testid="per-query-total">
            {centsToUSD(totalCents || 0)}
          </div>
        </div>
      </div>
      {!charges || charges.length === 0 ? (
        <div className="p-6 text-center text-text-secondary text-[13px]">
          No broker queries run this billing period yet.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border-default bg-bg-primary/40">
                <th className="jea-caption text-text-secondary p-4 text-left">Date</th>
                <th className="jea-caption text-text-secondary p-4 text-left">Debtor</th>
                <th className="jea-caption text-text-secondary p-4 text-left">Brokers</th>
                <th className="jea-caption text-text-secondary p-4 text-left">Bundle</th>
                <th className="jea-caption text-text-secondary p-4 text-right">Cost</th>
              </tr>
            </thead>
            <tbody>
              {charges.map((c, i) => (
                <motion.tr
                  key={c.id}
                  initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.18, delay: i * 0.02 }}
                  className="border-b border-border-default hover:bg-bg-tertiary transition-colors cursor-pointer"
                  onClick={() => onRowClick?.(c)}
                  data-testid={`per-query-row-${c.id}`}
                >
                  <td className="p-4 jea-date text-[13px]">{shortDate(c.started_at)}</td>
                  <td className="p-4 text-white text-[13px] font-semibold">{c.debtor_name || "—"}</td>
                  <td className="p-4 text-text-secondary text-[13px] jea-mono">{(c.broker_slugs || []).length}</td>
                  <td className="p-4 text-text-secondary text-[13px] capitalize">{(c.bundle || "custom").replace(/_/g, " ")}</td>
                  <td className="p-4 jea-mono text-right text-accent-pink font-bold text-[13px]">
                    {centsToUSD(c.total_cost_cents)}
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </JeaCard>
  );
};

const CancelBanner = ({ subscription, onReactivate, reactivating }) => (
  <motion.div
    initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
    className="rounded-[12px] border border-warning/50 p-4 flex items-start gap-3"
    style={{ background: "linear-gradient(180deg, rgba(243,156,18,0.08) 0%, rgba(243,156,18,0.02) 100%)" }}
    data-testid="billing-cancel-banner"
  >
    <AlertTriangle size={18} className="text-warning mt-0.5 shrink-0" />
    <div className="flex-1 text-[13px]">
      <div className="text-white font-semibold">Subscription is cancelling.</div>
      <div className="text-text-secondary mt-1">
        Access continues until <strong className="text-white jea-date">{shortDate(subscription?.cancels_at)}</strong>.
        Reactivate anytime before then to keep everything running without interruption.
      </div>
    </div>
    <JeaButton
      size="sm"
      variant="outline-pink"
      loading={reactivating}
      onClick={onReactivate}
      className="gap-2"
      data-testid="billing-reactivate"
    >
      <RefreshCcw size={12} /> Reactivate
    </JeaButton>
  </motion.div>
);

const BillingPage = () => {
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [perQuery, setPerQuery] = useState({ charges: [], total_cents: 0, period_start: null });
  const [perQueryLoading, setPerQueryLoading] = useState(true);
  const [changeOpen, setChangeOpen] = useState(false);
  const [changePreset, setChangePreset] = useState({ planType: null, cadence: null });
  const [pmOpen, setPmOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelSubmitting, setCancelSubmitting] = useState(false);
  const [reactivating, setReactivating] = useState(false);

  // Support deep-link openers such as
  //   /billing?open=change-plan&target=pro-monthly
  // from the "Upgrade to PRO" CTA on the sidebar + debtor pages.
  React.useEffect(() => {
    const openParam = searchParams.get("open");
    const target = searchParams.get("target");
    if (openParam === "change-plan") {
      let planType = null;
      let cadence = null;
      if (target === "pro-monthly")      { planType = "pro"; cadence = "monthly"; }
      else if (target === "pro-annual")  { planType = "pro"; cadence = "annual"; }
      else if (target === "pro")         { planType = "pro"; cadence = "monthly"; }
      setChangePreset({ planType, cadence });
      setChangeOpen(true);
      // Clean the URL so the modal doesn't re-open on state changes.
      const next = new URLSearchParams(searchParams);
      next.delete("open");
      next.delete("target");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadSummary = async () => {
    setSummaryLoading(true);
    try {
      const { data } = await apiClient.get("/billing/summary");
      setSummary(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSummaryLoading(false);
    }
  };

  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const { data } = await apiClient.get("/billing/history?limit=25");
      setHistory(data.events || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setHistoryLoading(false);
    }
  };

  const loadPerQuery = async () => {
    setPerQueryLoading(true);
    try {
      const { data } = await apiClient.get("/billing/per-query-charges");
      setPerQuery(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPerQueryLoading(false);
    }
  };

  useEffect(() => {
    loadSummary();
    loadHistory();
    loadPerQuery();
  }, []);

  const sub = summary?.subscription || null;
  const paymentMethod = sub?.payment_method || null;
  const isCanceling = sub?.status === "canceling";

  const cancelConfirm = async () => {
    setCancelSubmitting(true);
    try {
      const { data } = await apiClient.post("/subscription/cancel", { confirm: true });
      toast.success("Subscription is cancelling.");
      setSummary((s) => ({ ...(s || {}), subscription: data.subscription }));
      setCancelOpen(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCancelSubmitting(false);
    }
  };

  const reactivate = async () => {
    setReactivating(true);
    try {
      const { data } = await apiClient.post("/subscription/reactivate");
      toast.success("Subscription reactivated.");
      setSummary((s) => ({ ...(s || {}), subscription: data.subscription }));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setReactivating(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="billing-page">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div className="space-y-1">
          <div className="jea-caption">Subscription & Charges</div>
          <h1 className="text-[32px] font-extrabold tracking-[-0.02em] text-white">Billing</h1>
          <p className="text-text-secondary text-[14px]">
            Manage your plan, payment method, and review your billing history.
          </p>
        </div>
      </div>

      {isCanceling && <CancelBanner subscription={sub} onReactivate={reactivate} reactivating={reactivating} />}

      {/* Top row — Current Plan + Payment Method */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-4">
        {/* Current Plan */}
        {summaryLoading ? (
          <Skeleton className="h-[220px] rounded-[12px]" />
        ) : (
          <JeaCard data-testid="billing-current-plan">
            <div className="flex items-start justify-between">
              <div>
                <div className="jea-caption">Current Plan</div>
                <div className="flex items-center gap-3 mt-1">
                  <h2 className="text-white font-bold text-[22px]">{planLabel(sub)}</h2>
                  {sub && <StatusPill status={sub.status} />}
                </div>
                <div className="mt-2 flex items-baseline gap-2">
                  <div className="jea-mono text-accent-pink font-extrabold text-[28px]" data-testid="billing-plan-price">
                    {centsToUSD(sub?.price_cents || 0)}
                  </div>
                  <div className="text-text-muted text-[12px]">
                    {sub?.plan_type === "pro" && sub?.path === "annual" ? "/year" : "/period"}
                  </div>
                </div>
                {sub?.renews_at && !isCanceling && (
                  <div className="text-text-secondary text-[12px] mt-1">
                    Renews <span className="jea-date">{shortDate(sub.renews_at)}</span>
                  </div>
                )}
              </div>
              <div className="w-11 h-11 rounded-[8px] bg-accent-pink/10 border border-accent-pink/40 flex items-center justify-center shrink-0">
                <Sparkles size={20} className="text-accent-pink" />
              </div>
            </div>

            <div className="mt-5 flex items-center gap-2 flex-wrap">
              <JeaButton onClick={() => setChangeOpen(true)} className="gap-2" data-testid="billing-change-plan">
                <RefreshCcw size={14} /> Change Plan
              </JeaButton>
              {sub && !isCanceling && (
                <JeaButton variant="outline-neutral" onClick={() => setCancelOpen(true)} className="gap-2 !text-danger !border-danger/60 hover:!bg-danger hover:!text-white" data-testid="billing-cancel-plan">
                  <Ban size={14} /> Cancel
                </JeaButton>
              )}
              {isCanceling && (
                <span className="inline-flex items-center gap-1 text-[12px] text-warning">
                  <AlertTriangle size={12} /> Access ends {shortDate(sub?.cancels_at)}
                </span>
              )}
            </div>
          </JeaCard>
        )}

        {/* Payment Method */}
        {summaryLoading ? (
          <Skeleton className="h-[220px] rounded-[12px]" />
        ) : (
          <JeaCard data-testid="billing-payment-method-card">
            <div className="jea-caption">Payment Method</div>
            <div className="mt-3">
              <PaymentMethodDisplay pm={paymentMethod} onEdit={() => setPmOpen(true)} />
            </div>
            <div className="mt-4 rounded-[10px] bg-bg-primary border border-border-default p-3 flex items-start gap-2">
              <CheckCircle2 size={13} className="text-success shrink-0 mt-[3px]" />
              <p className="text-text-secondary text-[12px] leading-5">
                <strong className="text-white">Mocked Stripe.</strong> No real card is charged in this environment.
                Broker query charges are simulated but tracked accurately for reconciliation.
              </p>
            </div>
          </JeaCard>
        )}
      </div>

      {/* Per-Query */}
      <PerQueryTable
        charges={perQuery.charges}
        totalCents={perQuery.total_cents}
        periodStart={perQuery.period_start}
        loading={perQueryLoading}
        onRowClick={(c) => nav(`/debtors/${c.debtor_id}/queries/${c.id}`)}
      />

      {/* Billing History */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <ScrollText size={16} className="text-text-secondary" />
          <h2 className="text-white font-semibold text-[16px]">Billing History</h2>
        </div>
        <HistoryTable events={history} loading={historyLoading} />
      </div>

      <ChangePlanModal
        open={changeOpen}
        subscription={sub}
        initialPlanType={changePreset.planType}
        initialProCadence={changePreset.cadence}
        onClose={() => { setChangeOpen(false); setChangePreset({ planType: null, cadence: null }); }}
        onChanged={(s) => {
          setSummary((prev) => ({ ...(prev || {}), subscription: s }));
          loadHistory();
          loadPerQuery();
        }}
      />
      <PaymentMethodModal
        open={pmOpen}
        onClose={() => setPmOpen(false)}
        onSaved={(pm) => {
          setSummary((prev) => ({
            ...(prev || {}),
            subscription: { ...(prev?.subscription || {}), payment_method: pm },
          }));
        }}
      />
      <CancelSubscriptionModal
        open={cancelOpen}
        subscription={sub}
        submitting={cancelSubmitting}
        onCancel={() => setCancelOpen(false)}
        onConfirm={cancelConfirm}
      />
    </div>
  );
};

export default BillingPage;
